import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnInit, output, viewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { PlexEnvironment } from '@app/env';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { DisabledControlDirective } from '@app/shared/directives/disabled-control/disabled-control.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { nameof } from '@app/shared/utilities/javascript-utilities';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DatePickerComponent } from '@progress/kendo-angular-dateinputs';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { ProjectBaseScenarioTabItemComponent } from 'app/cem/project/content/project-base-scenario-tabitem.component';
import { ProjectCreateHelper } from 'app/cem/project/content/project-create-new/project-create.helper.service';
import { AssetWell, ISmallWell, ProjectApprovalMatrix, ProjectLists, TypeSubType, BatteryDesignType } from 'app/cem/project/content/project-create-new/project-create.models';
import { DataDescriptor } from 'app/cem/project/services/data-descriptor.model';
import { ProjectTemplateMatrixService, ProjectTemplateMetaFields } from 'app/cem/project/services/lookups/project-template-matrix.service';
import { AppStorage } from 'app/common/services/data-api/app-storage.service';
import { ErrorService } from 'app/common/services/error.service';
import { AssetTeamId, ConstructionTypes, PadConstructionProjectSubTypeId, ProjectDivision, ProjectTypeId, TankBatteryDesign } from 'app/shared/data/project-enums';
import { Asset, AssetTeam, Project, ProjectTypeScreenBehavior } from 'app/shared/models';
import { NameValueInt, NameValueIntParentInt, NameValueIntParentIntFunctionInt } from 'app/shared/models/name-value';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { ProjectDefaults } from '../../models/project-defaults';
import { GeneralProjectService } from '../../services/general-project.service';
import { LookupProjectTemplateService } from '../../services/lookups/lookup-project-template.service';
import { ProjectTabService } from '../../services/project-tab.service';
import { ProjectAssetMappingPipelineComponent } from '../project-asset-mapping/project-asset-mapping-pipeline/project-asset-mapping-pipeline.component';
import { ProjectAssetMappingTankBatteryComponent } from '../project-asset-mapping/project-asset-mapping-tank-battery/project-asset-mapping-tank-battery.component';
import { ProjectDrillGroupMappingComponent } from '../project-drill-group-mapping/project-drill-group-mapping.component';
import { LocalStorageService } from './../../../../auth/local-storage.service';
import { DrillGroup } from './../../../../shared/models/schedule/drillgroup';

@Component({
  selector: 'app-project-create-new',
  templateUrl: './project-create-new.component.html',
  styleUrls: ['./project-create-new.component.scss'],
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    ComboBoxComponent,
    DatePickerComponent,
    DisabledControlDirective,
    FormsModule,
    ProjectAssetMappingTankBatteryComponent,
    ProjectAssetMappingPipelineComponent,
    ProjectDrillGroupMappingComponent,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    ProjectCreateHelper,
    ProjectTemplateMatrixService, //Needed for ProjectCreateHelper
    LookupProjectTemplateService, //Needed for ProjectTemplateMatrixService
  ],
})
export class ProjectCreateNewComponent extends ProjectBaseScenarioTabItemComponent implements OnInit, AfterViewInit {
  public helper = inject(ProjectCreateHelper);
  private fb = inject(UntypedFormBuilder);
  private _elRef = inject(ElementRef);
  public store = inject(AppStorage);
  private errorService = inject(ErrorService);
  private ref = inject(ChangeDetectorRef);
  public projectTabService = inject(ProjectTabService);
  public localStorage = inject(LocalStorageService);
  private generalProjectService = inject(GeneralProjectService);
  public environment = inject(PlexEnvironment);

  private selectingSingles: boolean = false;
  private lastFormValidCheck: boolean = false;
  private UnfilteredDataSources: ProjectTemplateMetaFields; //{[key:string]: NameValueInt[]} = {};
  private resetMatrix: boolean = true;
  private projectNameValidatorRegex: RegExp = /[\"/\\\\<>,:|*?]/;
  private scenarioWellCount: number = 0;
  private assetCount: number = 0;
  private wellSelectorCount: number = 0;
  private minDate: Date = new Date(2006, 2, 10);
  private subTypeIsRequired: boolean;
  private postStimDefaultValues: any;
  public icons = kendoIcons;
  public DataSources: ProjectTemplateMetaFields; //{[key:string]: NameValueInt[]} = {};
  public OtherDataSources: ProjectLists;
  public OtherFunctionFilteredProjectTeams = new Array<NameValueIntParentIntFunctionInt>();
  public AfeApprovalTeamsData = new Array<ProjectApprovalMatrix>();
  public AfeApprovalTeamsFiltered: ProjectApprovalMatrix[] | null = null;
  public BatteryDesignTypes = new Array<BatteryDesignType>();
  public dAssetWells = new Array<AssetWell>();
  public dDrillGroups = new Array<DrillGroup>();
  public dTBAssets = new Array<Asset>();
  public dPPIDs = new Array<Asset>();
  public isFalseNonOperating: boolean = true;
  public selectedProjectType: number;
  public selectedWell: number;
  public selectedDrillGroup: number;
  public additionalDrillgroups: Array<number> = [];
  public assetTypes = new Array<NameValueInt>();
  public areThereSubtypes: boolean = false;
  public createProjectForm: UntypedFormGroup;
  public showWellSelectors: boolean = false;
  public showDrillGroups: boolean = false;
  public showStartDate: boolean = true;
  public showFinishDate: boolean = true;
  public createProjectFormSubmitted: boolean = false;
  public specialDataSources: SpecialControls | any = { ConstructionType: DataDescriptor.namevalue(), ConstructionSequence: DataDescriptor.namevalue() };
  public wellRelatedProjects: Array<number> = [21, 58, 59];
  public wellSelectionMustProjects: Array<number> = [21, 58, 59];
  public selectedScenario: number;
  public selectedAssetTeam: number;
  public selectedProjectSubType: string;
  public selectedProjectSubTypeId: number;
  public selectedExploreDevelopId: number;
  public allocatedAssets: Array<number> = [];
  public preSelectedAssets: Array<any> = [];
  public selectedAssets: Array<any> = [];
  public showPostStimCheckbox: boolean = false;
  public showCreateProjectsForWellsInGroup: boolean = false;
  public notifyTBAssetChange: Subject<any> = new Subject();
  public isDGRelated: boolean = false;
  public hideTankBattery: boolean = false;
  public hidePPID: boolean = true;
  public isPadConstructionProject: boolean = false;
  public refreshClicked: boolean = false;
  public approvalTeamUrl: string | null = null;
  public ProjectTypeId = ProjectTypeId;
  readonly wellDropDown = viewChild<ComboBoxComponent>('WellIdsCombo');
  readonly dgDropDown = viewChild<ComboBoxComponent>('DgIdsCombo');
  readonly TbAssetsDropDown = viewChild<ComboBoxComponent>('TbIdsCombo');
  readonly PPIDAssetsDropDown = viewChild<ComboBoxComponent>('PPIDsCombo');
  readonly projectDrillGroupMappingComponent = viewChild(ProjectDrillGroupMappingComponent);

  handleFilter(filter: string, dataSource: any[], filteredSourceName: string, fieldName: string) {
    const contains = value => s => s[fieldName].toLowerCase().indexOf(value.toLowerCase()) !== -1;
    let filteredSource = dataSource.filter(contains(filter));
    this[filteredSourceName] = filteredSource;
  }

  public controlsConfig: IControls = {
    projectName: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(69),
    projectNameValidator(this.projectNameValidatorRegex), projectNameInvalidCharValidator()]],
    FunctionIds: [null],
    AfeApprovalTeam: [null, [Validators.required]],
    pioneerAssetTeam: [null, { disabled: true }],
    ProjectTypeIds: [null, [Validators.required]],
    DivisionIds: [null],
    ProjectSubTypeIds: [null, [Validators.required]],
    TypeCurveAreaIds: [null],
    ProjectTeamIds: [null],
    BusinessAssociateIds: [null, [Validators.required]],
    IsNonOperating: [true],
    NeedByDate: [null],
    StartDate: [null, [projectMinDateValidator(this.minDate)]],
    ConstructionSequenceIds: [null],
    ConstructionTypeIds: [null],
    WellProfileIds: [null, [Validators.required]],
    TargetFormationIds: [null],
    WellIds: [null],
    DrillGroupIds: [null],
    TankBatteryAssetId: [null],
    PadAssetId: [null],
    projectDescription: [''],
    Location: [null],
    CreatePostStimProject: [{ value: false, disabled: true }],
    CreateProjectsForWellsInGroup: [null],
    ExploreDevelopIds: [null, [Validators.required]],
    BatteryDesignTypes: [null]
  };

  public fieldVisibility: IControls | any = {
    projectName: true,
    projectDescription: true,
    Location: true,
    FunctionIds: true,
    pioneerAssetTeam: true,
    ProjectTypeIds: true,
    DivisionIds: true,
    ProjectSubTypeIds: true,
    TypeCurveAreaIds: true,
    ProjectTeamIds: true,
    BusinessAssociateIds: true,
    IsNonOperating: true,
    NeedByDate: true,
    StartDate: true,
    ConstructionSequenceIds: true,
    ConstructionTypeIds: true,
    WellProfileIds: true,
    TargetFormationIds: true,
    WellIds: true,
    DrillGroupIds: true,
    TankBatteryAssetId: true,
    PadAssetId: false,
    showAssetTypeSelector: false,
    CreatePostStimProject: true,
    CreateProjectsForWellsInGroup: true,
    ExploreDevelopIds: true
  };

  public status: {
    running: boolean;
    action: string
  } = {
      running: true,
      action: 'Loading Data...'
    };

  readonly assetTeamId = output<number>();
  readonly doesProjectExist = output<boolean>();
  readonly shouldShowNewProject = output<boolean>();
  readonly isValidNewProject = output<boolean>();
  readonly hideWbsCodingTab = output<boolean>();
  readonly setParameterTabVisibility = output<TypeSubType>();

  constructor() {
    super();
    this.UnfilteredDataSources = this.helper.matrixService.items;
    this.DataSources = this.helper.matrixService.filteredItems;
    this.OtherDataSources = this.helper.FilteredLists;
  }

  ngAfterViewInit() {
    const wellDropDown = this.wellDropDown();
    if (!wellDropDown) return;

    wellDropDown.filterChange.asObservable().pipe(
      debounceTime(500),
      filter((x: string) => x.length > 2 && !!x),
      distinctUntilChanged(),
      tap(() => this.wellDropDown().loading = true),
      switchMap(async x =>
        await this.handleWellFilter(x as any)
      )
    )
      .subscribe(async x => {
        this.dAssetWells = x;
        this.wellDropDown().loading = false;
        this.ref.markForCheck();
      });

    this.dgDropDown().filterChange.asObservable().pipe(
      filter((x: string) => x.length > 2),
      tap(() => this.dgDropDown().loading = true),
      debounceTime(500),
      distinctUntilChanged(),
    )
      .subscribe(async x => {
        this.dDrillGroups = await this.handleDrillGroupFilter(x);
        this.dgDropDown().loading = false;
        this.ref.markForCheck();
      });

    this.TbAssetsDropDown().filterChange.asObservable().pipe(
      filter((x: string) => x.length > 2),
      tap(() => this.TbAssetsDropDown().loading = true),
      debounceTime(500),
      distinctUntilChanged(),
    )
      .subscribe(async x => {
        this.dTBAssets = await this.handleTankBatteryAssetsFilter(x);
        this.TbAssetsDropDown().loading = false;
        this.ref.markForCheck();
      });

    this.PPIDAssetsDropDown().filterChange.asObservable().pipe(
      filter((x: string) => x.length > 2),
      tap(() => this.PPIDAssetsDropDown().loading = true),
      debounceTime(500),
      distinctUntilChanged(),
    )
      .subscribe(async x => {
        this.dPPIDs = await this.handlePPIDAssetsFilter(x);
        this.PPIDAssetsDropDown().loading = false;
        this.ref.markForCheck();
      });
  }

  async ngOnInit() {
    this.resetHelperServiceProjectTypeAndSubtype()
    this.generateForm();
    await this.helper.init();
    this.setupForm();

    this.store.knownEvents.assetTeamSelected.subscribe(async t => {
      if (t) {
        await this.filterAssetTeam(t);
      }
    });

    this.helper.draftEstimateCreated.subscribe(e => {
      if (!e) return;
      this.setProjectTabFlags();
    });

    this.helper.projectCreated.subscribe((e: Project) => {
      if (e) {
        this.showStartDate = true;
      }
    });

    this.helper.projectCreating.subscribe((x: { status: string, percent: number }) => {
      if (!x) return;
      this.status.action = x.status;
      if (x.percent >= 100) {
        window.setTimeout(() => {
          //this way the user sees the last message for 5 seconds before it hides
          this.status.running = false;
        }, 5000);
      } else {
        this.status.running = true;
      }
    });

    if (this.helper.servicesLoaded) {
      this.processEngineResults();
    } else {
      this.helper.start().subscribe(r => {
        this.processEngineResults();
      });
    }

    this.store.knownEvents.scenarioSelected.subscribe(async t => {
      //NOTE: t may not have a value, if it does it will be the Scenario Object
      if (t) {
        this.helper.Selections.scenario = t;
        this.load();
        this.selectedScenario = t.ScenarioId;
        this.selectedAssetTeam = t.AssetTeamId;
        this.scenarioWellCount = await this.helper.getScenarioWellCount(t.ScenarioId);
        this.assetCount = await this.helper.getAssetCount(t.AssetTeamId);

        if (this.helper.WellFilters.showingAssets) {
          this.wellSelectorCount = this.assetCount;
        } else {
          this.wellSelectorCount = this.scenarioWellCount;
        }
        if (this.wellSelectorCount && this.helper.WellFilters.haveWellData) {
          this.dAssetWells = await this.getAssetWells();
        }
      } else {
        this.scenarioWellCount = 0;
        this.assetCount = 0;
        this.wellSelectorCount = 0;
      }
      if (!this.wellSelectorCount) {
        this.helper.WellFilters.wells = [];
      }
    });

    this.createProjectForm.valueChanges.subscribe(data => this.onFormValueChanged(data));
    this.createProjectForm.controls.ProjectTypeIds.valueChanges.subscribe(data => { this.filterProjectType(data), this.setDefaultBatteryDesignType() });
    this.createProjectForm.controls.IsNonOperating.valueChanges.subscribe(data => this.handleNonOperatingChange(data));
    this.createProjectForm.controls.ProjectSubTypeIds.valueChanges.subscribe(data => this.handleSubTypeSelection(data));
    this.createProjectForm.controls.ExploreDevelopIds.valueChanges.subscribe(data => this.handleExploreDevelopSelection(data));
    this.createProjectForm.controls.DrillGroupIds.valueChanges.subscribe(data => this.handleDrillGroupSelection(data));
    this.createProjectForm.controls.WellProfileIds.valueChanges.subscribe(data => this.handleWellTypeSelectChange(data, 'WellProfileId'));
    this.createProjectForm.controls.AfeApprovalTeam.valueChanges.subscribe(data => this.filterAfeApprovalTeam(data));
    this.onFormValueChanged();

    this.createProjectForm.patchValue({
      IsNonOperating: {
        text: 'Yes',
        value: true
      }
    });

    this.helper.WellFilters.haveWellData = false;
    this.isFalseNonOperating = false;
    this.createProjectForm.controls.BusinessAssociateIds.disable();
    this.createProjectForm.controls.ProjectTeamIds.disable();
    this.createProjectForm.controls.pioneerAssetTeam.disable();
    this.createProjectForm.controls.TypeCurveAreaIds.disable();
    this.helper.getBatteryDesignTypeList().then(resp => {
      this.BatteryDesignTypes = resp;
      if (this.selectedProjectType === ProjectTypeId.TankBattery &&
        !this.createProjectForm.controls.BatteryDesignTypes.value) {
        this.setDefaultBatteryDesignType();
      }
    });
  }

  setDefaultBatteryDesignType() {
    const control = this.createProjectForm.controls.BatteryDesignTypes;
    if (this.selectedProjectType === ProjectTypeId.TankBattery) {
      control.enable();
      control.setValidators([Validators.required]);
      control.setValue(this.BatteryDesignTypes[0]);
    } else {
      control.clearValidators();
      control.disable();
      control.setValue(null);
    }
    control.updateValueAndValidity();
  }

  handleRowSelected(event: any) {
    let dgsSelected = this.projectDrillGroupMappingComponent().drillGroupsSelectionGrid().api.getSelectedRows();

    if (dgsSelected.length <= 0 && !this.status.running) {
      this.additionalDrillgroups = [];
      this.resetProjectDefaults(true);
      return;
    }

    const dgWithMinSpudDate = dgsSelected.reduce((min, current) =>
      new Date(current.MinSpudDate) < new Date(min.MinSpudDate) ? current : min
    );

    if (dgsSelected.length <= 0 && !this.status.running) {
      this.selectedDrillGroup = 0;
    } else if (!this.status.running && this.selectedDrillGroup !== dgWithMinSpudDate.DrillGroupId) {
      this.selectedDrillGroup = dgWithMinSpudDate.DrillGroupId;
      this.setProjectDefaults();
    }
    this.additionalDrillgroups = dgsSelected.filter(dg => dg.DrillGroupId !== this.selectedDrillGroup).map(dg => dg.DrillGroupId);
  }

  private load() {
    let assetTeam = this.helper.Selections.assetTeam;
    if (assetTeam) {
      this.filterAssetTeam(assetTeam);
    }
  }

  private processEngineResults() {
    this.load();
    this.status.running = false;
    this.clearSelections(false);
    this.handleProjectTypeChange(null);
    this.selectSingles();

    if (this.localStorage.getItem('tb-selected-wells').value) {
      setTimeout(() => {
        let subProjectId = this.localStorage.getItem<any>('tb-selected-wells').value.key;
        this.preSelectedAssets = this.localStorage.getItem<any>('tb-selected-wells').value.wells;
        this.createProjectForm.controls.ProjectTypeIds.setValue(this.DataSources.ProjectTypeIds.filter(s => s.value === 1)[0]);
        this.createProjectForm.controls.ProjectSubTypeIds.setValue(this.DataSources.ProjectSubTypeIds.filter(s => s.value === Number(subProjectId))[0]);

        this.localStorage.removeItem('tb-selected-wells');
      }, 1000);
    }
  }

  private async getAssetWells(filterName?: string): Promise<AssetWell[]> {
    let name = '';
    if (filterName && filterName !== '' && filterName.length > 2) {
      name = filterName;
    }
    else {
      return;
    }
    let result = await this.helper.getAssetsWellData(name);
    this.wellDropDown().loading = false;
    return result;
  }

  private async getDrillgroups(filterName?: string): Promise<DrillGroup[]> {
    let name = '';
    if (filterName && filterName !== '') {
      name = filterName;
    }
    let result = await this.helper.getDrillGroupsData(name);
    this.dgDropDown().loading = false;
    return result;
  }

  private async getAfeProjectTeams(assetTeamId: number, projectTypeId: number, projectSubTypeId: number, exploreDevelopId: number): Promise<ProjectApprovalMatrix[]> {
    try {
      const result = await this.helper.getAfeProjectTeams(assetTeamId, projectTypeId, projectSubTypeId, exploreDevelopId);
      return result;
    } catch (error) {
      return [];
    }
  }

  private async getTankBatteryAssets(filterName?: string): Promise<Asset[]> {
    let name = '';
    if (filterName && filterName !== '') {
      name = filterName;
    }
    let result = await this.helper.getTankBatteryAssets(name);
    this.TbAssetsDropDown().loading = false;
    return result;
  }

  private async getPPIDAssets(filterName?: string): Promise<Asset[]> {
    let name = '';
    if (filterName && filterName !== '') {
      name = filterName;
    }
    let result = await this.helper.getPPIDAssets(name);
    this.PPIDAssetsDropDown().loading = false;
    return result;
  }

  private setDefaultAfeApprovalProjectTeamList() {
    this.createProjectForm.controls.AfeApprovalTeam.setValue(null);
    if (!this.helper.Selections.assetTeam) return;
    let assetTeamId: number = this.helper.Selections.assetTeam.AssetTeamId;
    let projectTypeId: number = this.helper.Selections.projectTypeId;
    let projectSubTypeId: number = this.helper.Selections.projectSubTypeId;
    let exploreDevelopId: number = this.helper.Selections.exploreDevelopId;
    this.getAfeProjectTeams(assetTeamId, projectTypeId, projectSubTypeId, exploreDevelopId)
      .then(resp => {
        this.AfeApprovalTeamsData = resp;
        this.AfeApprovalTeamsFiltered = resp;
      })
      .catch(error => {
        console.error("Error resolving promise:", error);
      });

  }

  private refreshDefaultProjectTeamList(selected: NameValueInt) {
    let assetTeamId: number = this.helper.Selections.assetTeam.AssetTeamId;
    let fValue: any = selected.value;
    this.OtherFunctionFilteredProjectTeams = this.OtherDataSources.ProjectTeamIds.filter(s => s.functionValue === fValue && s.name !== 'Custom');
    this.OtherFunctionFilteredProjectTeams.unshift({
      name: 'Custom',
      value: null,
      parentValue: assetTeamId,
      functionValue: fValue
    });
    if (this.OtherFunctionFilteredProjectTeams.length > 0) {
      this.createProjectForm.controls.ProjectTeamIds.enable();
    } else {
      this.createProjectForm.controls.ProjectTeamIds.disable();
    }
  }

  private generateForm() {
    let StartDateRequired = this.showStartDate ? [Validators.required, projectMinDateValidator(this.minDate)] : null;
    let finishDateRequired = this.showFinishDate ? [Validators.required, this.endDateAfterStart] : null;

    this.controlsConfig.NeedByDate = [null, finishDateRequired];
    this.controlsConfig.StartDate = [null, StartDateRequired];

    this.controlsConfig.pioneerAssetTeam = [null, {
      value: this.store.knownStore.assetTeam ? this.store.knownStore.assetTeam.AssetTeamCode : '',
      disabled: true
    }];

    this.createProjectForm = this.fb.group(this.controlsConfig);   //flaky, {validator: this.endDateAfterStart.bind(this)});
  }

  private setupForm() {
    //MAP the fields from the database to the control form field names
    let friendlyMap: { [key: string]: string } = {
      'Construction Sub Type': 'ConstructionSequenceIds',
      'Construction Type': 'ConstructionTypeIds',
      'Development Area': 'TypeCurveAreaIds',
      'Division': 'DivisionIds',
      'Function': 'FunctionIds',
      'WBS Approval Team': 'AfeApprovalTeam',
      'Planned Project Finish Date': 'NeedByDate',
      'Planned Project Start Date': 'StartDate',
      'Project Sub Type': 'ProjectSubTypeIds',
      'Project Team': 'ProjectTeamIds',
      'Project Type': 'ProjectTypeIds',
      'Drill Group': 'DrillGroupIds',
      'Explore / Develop Description': 'ExploreDevelopIds',
      'Battery Design Type': 'BatteryDesignTypes'
    };
    this.helper.setup(this.createProjectForm, this.controlsConfig, this._elRef);
    this.helper.screenBehaviors.setup(this._elRef, this.controlsConfig, friendlyMap, this.createProjectForm, this.fieldVisibility);
  }

  private onFormValueChanged(data?: any) {
    let isValid: boolean = false;

    if (!this.createProjectForm) {
      return;
    }

    if (this.createProjectForm.valid) {
      isValid = true;
    }
    if (isValid !== this.lastFormValidCheck) {
      this.lastFormValidCheck = isValid;
      this.isValidNewProject.emit(isValid);
    }
  }

  private setProjectTabFlags(): void {
    this.doesProjectExist.emit(true);
    this.shouldShowNewProject.emit(false);
  }

  private callCreateProject(project: Project): void {
    this.projectTab().Project = project;
    this.projectTab().ProjectChanged.emit(project);
    this.doesProjectExist.emit(true);
    this.shouldShowNewProject.emit(false);
    let typeSubType: TypeSubType = {
      ProjectTypeId: project.ProjectTypeId,
      ProjectSubTypeId: project.ProjectSubTypeId,
      FunctionId: project.FunctionId
    };

    this.setParameterTabVisibility.emit(typeSubType);
  }

  private async handleWellTypeSelectChange(e: NameValueInt, fieldName: string) {
    let id: number = 0;
    if (e) {
      id = e.value;
    }
    let helper = this.helper;
    helper.WellFilters[fieldName] = id;

    this.helper.WellFilters.haveWellData =
      (!this.fieldVisibility.showAssetTypeSelector || this.helper.WellFilters.AssetTypeId > 0);

    if (this.showWellSelectors && this.helper.WellFilters.haveWellData && this.helper.Selections.assetTeam) {
      this.dAssetWells = await this.getAssetWells();
    }
  }

  private controlName(param: (type: IControls) => string): string {
    return nameof<IControls>(param);
  }

  private _ProjectDefaults: ProjectDefaults = null;

  private setProjectDefaults() {
    this.helper.getProjectDefaults(this.selectedProjectType, this.helper.Selections.scenario.ScenarioId,
      this.selectedWell, this.selectedDrillGroup).then(pd => {
        this._ProjectDefaults = pd;
        this.createProjectForm.controls.projectName.setValue(pd.Name);
        this.createProjectForm.controls.projectDescription.setValue(pd.ProjectDescription);
        this.createProjectForm.controls.Location.setValue(pd.Location);
        this.createProjectForm.controls.DivisionIds.setValue(this.DataSources.DivisionIds.filter(s => s.value === pd.DivisionId)[0]);
        if (this.selectedProjectType === ProjectTypeId.DrillOilGasWell || this.selectedProjectType === ProjectTypeId.CompletePostStimOAG || this.selectedProjectType === ProjectTypeId.CompleteStimOAG) {
          this.createProjectForm.controls.ProjectSubTypeIds.setValue(this.UnfilteredDataSources.ProjectSubTypeIds.filter(x => x.value === pd.ProjectSubTypeId)[0]);
        }
        if (pd.ConstructionSequenceId)
          this.createProjectForm.controls.ConstructionSequenceIds.setValue(this.DataSources.ConstructionSequenceIds.filter(s => s.value === pd.ConstructionSequenceId)[0]);
        if (pd.ConstructionTypeId !== null)
          this.createProjectForm.controls.ConstructionTypeIds.setValue(this.DataSources.ConstructionTypeIds.filter(s => s.value === pd.ConstructionTypeId)[0]);
        this.createProjectForm.controls.ProjectTeamIds.setValue(this.OtherDataSources.ProjectTeamIds.filter(s => s.value === pd.ProjectTeamId)[0]);
        if (pd.FunctionId !== null)
          this.createProjectForm.controls.FunctionIds.setValue(this.DataSources.FunctionIds.filter(s => s.value === pd.FunctionId)[0]);
        if (pd.ProjectStart !== null)
          this.createProjectForm.controls.StartDate.setValue(new Date(pd.ProjectStart));
        if (pd.ProjectFinish !== null)
          this.createProjectForm.controls.NeedByDate.setValue(new Date(pd.ProjectFinish));

        if (this.selectedProjectType == ProjectTypeId.Flowline) {
          this.createProjectForm.controls.StartDate.setValue(null);
          this.setProjectEndDateFromAssets();
        }
        this.createProjectForm.updateValueAndValidity();
      });

    if (this.selectedProjectType === ProjectTypeId.CompleteStimOAG) {
      this.helper.getProjectDefaults(ProjectTypeId.CompletePostStimOAG, this.helper.Selections.scenario.ScenarioId, this.selectedWell, this.selectedDrillGroup).then(s => {
        this.postStimDefaultValues = s;
      });
    }
  }

  private resetProjectDefaults(preserveSubType: boolean = false) {
    this.selectedDrillGroup = null;
    this.selectedWell = null;
    this.approvalTeamUrl = null;
    this.wellDropDown().reset();
    this.dgDropDown().reset();
    this.TbAssetsDropDown().reset();
    this.PPIDAssetsDropDown().reset();
    const patchData: any = {
      projectName: '',
      projectDescription: '',
      DivisionIds: null,
      AfeApprovalTeam: null,
      ConstructionSequenceIds: null,
      ConstructionTypeIds: null,
      ProjectTeamIds: null,
      StartDate: null,
      NeedByDate: null,
      ExploreDevelopIds: null
    };
    if (!preserveSubType) {
      patchData.ProjectSubTypeIds = null;
    }
    this.createProjectForm.patchValue(patchData)
    this.createProjectForm.updateValueAndValidity();
  }

  private async handleDrillGroupSelection(value: any) {
    if (value && value.Description) {
      this.selectedDrillGroup = value.DrillGroupId;
      this.setProjectDefaults();
    }
  }
  public refreshAFEApprovalTeams(useNewEngine: boolean) {
    try {
      if (this.selectedProjectType !== undefined) {
        this.refreshAfeApprovalProjectTeamList();
      }
    }
    catch (error) {
      console.error("Failed to refresh AFE project teams:", error);
    }
  }

  private refreshAfeApprovalProjectTeamList(): void {
    this.refreshClicked = true;
    this.generalProjectService.refreshProjectApprovalMatrix()
      .subscribe({
        next: (result) => {
          // Handle success
          this.setDefaultAfeApprovalProjectTeamList();
          this.refreshClicked = false;
        },
        error: (err) => {
          // Handle error
          this.refreshClicked = false;
          console.error('Error refreshing matrix:', err);
        }
      });
  }

  private async handleProjectTypeChange(value: NameValueInt) {
    if (value && value.value)
      this.resetProjectDefaults();
    let showFinishDate: boolean;
    let oldType = this.selectedProjectType;
    let ptId = value && value.value;
    let form = this.createProjectForm;
    let pt = this.OtherDataSources.ProjectTypesRequiringWells.find(x => x.value === ptId);
    let requiresWell = pt !== undefined;
    this.helper.Selections.projectTypeId = ptId;
    // create Post Stim Project Checkbox Controls
    if (ptId === ProjectTypeId.CompleteStimOAG) {
      this.createProjectForm.controls.CreatePostStimProject.enable();
      this.showPostStimCheckbox = true;
    } else {
      this.createProjectForm.controls.CreatePostStimProject.setValue(false);
      this.createProjectForm.controls.CreatePostStimProject.disable();
      this.showPostStimCheckbox = false;
    }
    // create all well project in group controls
    let showWellCheckComparators = [
      ProjectTypeId.DrillOilGasWell,
      ProjectTypeId.CompletePostStimOAG,
      ProjectTypeId.CompleteStimOAG,
    ];
    if (showWellCheckComparators.includes(ptId)) {
      this.createProjectForm.controls.CreateProjectsForWellsInGroup.enable();
      this.showCreateProjectsForWellsInGroup = true;
    }
    else {
      this.createProjectForm.controls.CreateProjectsForWellsInGroup.setValue(false);
      this.createProjectForm.controls.CreateProjectsForWellsInGroup.disable();
      this.showCreateProjectsForWellsInGroup = false;
    }

    this.isPadConstructionProject = (ptId === ProjectTypeId.PadConstruction);
    this.hideTankBattery = this.isPadConstructionProject;
    this.hidePPID = !this.hideTankBattery;

    this.isDGRelated = (ptId === ProjectTypeId.WellHookup || ptId === ProjectTypeId.WellPadFacility || ptId === ProjectTypeId.PadConstruction);

    if (ptId === ProjectTypeId.TankBattery) {
      this.createProjectForm.controls.FunctionIds.setValue(this.DataSources.FunctionIds.filter(s => s.value === 1)[0]);
    }
    if (ptId === ProjectTypeId.Pipeline || ptId === ProjectTypeId.Flowline || ptId === ProjectTypeId.TankBattery) {
      this.showStartDate = false;
    }
    else {
      this.showStartDate = true;
    }

    const useExploreDevelopIdsWhen = [
      ProjectTypeId.CompleteStimOAG,
      ProjectTypeId.CompletePostStimOAG,
      ProjectTypeId.DrillOilGasWell,
      ProjectTypeId.LandPreDrilling,
      ProjectTypeId.GeologicalAndGeophysical
    ];
    let isExplDevPt = useExploreDevelopIdsWhen.includes(ptId);

    // Default to Development when Project Type is in useExploreDevelopIdsWhen but allow change by user
    this.createProjectForm.controls.ExploreDevelopIds.setValue(this.OtherDataSources.ExploreDevelopIds.filter(s => s.value === 2)[0]);
    this.createProjectForm.controls.ExploreDevelopIds.enable();

    requiresWell = this.wellRelatedProjects.includes(ptId) && this.wellSelectionMustProjects.includes(ptId); //requiresWell && !isDGRelated;
    this.showWellSelectors = this.wellRelatedProjects.includes(ptId);

    if (ptId) {
      this.selectedProjectType = value.value;
      showFinishDate = !requiresWell;
    } else {
      this.selectedProjectType = undefined;
      showFinishDate = false;
    }

    if (this.showWellSelectors !== requiresWell) {
      if (!this.showWellSelectors && form.controls.WellIds.value) {
        form.controls.WellIds.setValue(null);
      }
      this.showWellSelectors = this.wellRelatedProjects.includes(ptId);
    }

    this.showFinishDate = showFinishDate;

    this.DataSources.ProjectSubTypeIds = this.UnfilteredDataSources.ProjectSubTypeIds.filter(x => x.parentValue === ptId);
    this.areThereSubtypes = this.DataSources.ProjectSubTypeIds.length > 0;

    if (oldType !== this.selectedProjectType) {
      let result = this.helper.urls.assetTypeSelector(this.selectedProjectType);

      this.fieldVisibility.showAssetTypeSelector = result.showAssetSelector;
      this.assetTypes = result.availableAssetTypes;
      this.helper.Selections.projectTypeId = this.selectedProjectType;

      await this.helper.screenBehaviors.apply(this.selectedProjectType || 0, this._elRef,
        (fieldName: string, friendlyName: string, control: AbstractControl, behavior: ProjectTypeScreenBehavior): ProjectTypeScreenBehavior => {
          /*
              ******************************************************************
              //HERE IS WHERE YOU OVERRIDE THE SCREEN BEHAVIOR RULES ENFORCEMENT
              ******************************************************************
          */
          if (control === form.controls.AssetTypeIds) {
            behavior.Required = result.showAssetSelector;
          }

          // For ProjectSubType, if SubType is required, but the data array is empty and the field is required, then need to override the normal rule
          if (fieldName === this.controlName(w => w.ProjectSubTypeIds)) {
            if (behavior.Required === true) {
              this.subTypeIsRequired = true;
            }
            else {
              this.subTypeIsRequired = false;
            }
          }
          // --- ConstructionType ---Most of the dropdowns on Create use dboProjectTemplates for dropdown list available values.
          // For ConstructionType, use ScreenBehaviors instead.

          if (fieldName === this.controlName(w => w.ConstructionTypeIds)) {
            this.specialDataSources.ConstructionType.source = [];
            this.specialDataSources.ConstructionType.filtered = [];
            let dataSource = this.UnfilteredDataSources.ConstructionTypeIds;
            for (let i = 0; i < dataSource.length; i++) {
              let p = dataSource[i];
              if (p) {
                this.specialDataSources.ConstructionType.source.push(p);
                this.specialDataSources.ConstructionType.filtered.push(p);
              }
            }
            let defaultValue = behavior.DefaultValue;
            let defaultConstructionTypeDropdownSelection = null;
            if (defaultValue !== null) {
              defaultConstructionTypeDropdownSelection = this.specialDataSources.ConstructionType.filtered.find(x => x.name === defaultValue);
            }
            this.createProjectForm.patchValue({
              ConstructionTypeIds: defaultConstructionTypeDropdownSelection
            });
          }

          // The 3 PIG project types (Pond Build, PWM Env Remediation, General Env PWM) are moved under PAT basin only in Dev & QA.
          // The below "if" block is not valid/applicable for PROD environment.
          if (this.environment.hideAllButPATAndDELBasins && this.selectedAssetTeam === AssetTeamId.PAT && behavior.DefaultValue !== null &&
            (ptId === ProjectTypeId.PondBuild || ptId === ProjectTypeId.PWMEnvRemediation || ptId === ProjectTypeId.GeneralEnvPWM)) {
            const controlMap = [
              { field: this.controlName(w => w.DivisionIds), formControl: 'DivisionIds', dataSource: this.UnfilteredDataSources.DivisionIds },
              { field: this.controlName(w => w.FunctionIds), formControl: 'FunctionIds', dataSource: this.UnfilteredDataSources.FunctionIds }
            ];

            for (const item of controlMap) {
              if (fieldName === item.field) {
                const defaultSelection = item.dataSource.find(x => x.name.toUpperCase() === behavior.DefaultValue.toUpperCase()) || null;
                this.createProjectForm.patchValue({ [item.formControl]: defaultSelection });
                break;
              }
            }
          }

          if (fieldName === this.controlName(w => w.ConstructionSequenceIds)) {
            this.specialDataSources.ConstructionSequence.source = [];
            this.specialDataSources.ConstructionSequence.filtered = [];
            let dataSource = this.UnfilteredDataSources.ConstructionSequenceIds;
            for (let i = 0; i < dataSource.length; i++) {
              let p = dataSource[i];
              if (p) {
                this.specialDataSources.ConstructionSequence.source.push(p);
                this.specialDataSources.ConstructionSequence.filtered.push(p);
              }
            }

            let defaultValue = behavior.DefaultValue;
            let defaultConstructionSequenceDropdownSelection = null;
            if (defaultValue !== null) {
              defaultConstructionSequenceDropdownSelection = this.specialDataSources.ConstructionSequence.filtered.find(x => x.name === defaultValue);
            }
            this.createProjectForm.patchValue({
              ConstructionSequenceIds: defaultConstructionSequenceDropdownSelection
            });
          }

          if (fieldName === this.controlName(w => w.WellIds)) {
            behavior.Visible = requiresWell;
            behavior.Required = requiresWell;
          }
          return behavior;
        }
      );
      this.resetFormControls(showFinishDate, requiresWell);
    } else {
      this.resetFormControls(showFinishDate, requiresWell);
    }

    const projectDrillGroupMappingComponent = this.projectDrillGroupMappingComponent();
    if (this.isDGRelated && projectDrillGroupMappingComponent) {
      projectDrillGroupMappingComponent.loadData(ptId);
    }
  }

  private setValidation(control: AbstractControl, required: boolean) {
    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  private resetFormControls(showFinishDate: boolean, requiresWell: boolean) {
    let form = this.createProjectForm;
    this.setValidation(form.controls.StartDate, this.showStartDate);
    this.setValidation(form.controls.NeedByDate, showFinishDate);
    this.setValidation(form.controls.WellIds, requiresWell);
    this.setValidation(form.controls.DivisionIds, false);
    this.setValidation(form.controls.FunctionIds, false);
    this.setValidation(form.controls.ProjectTeamIds, false);
    this.setValidation(form.controls.WellProfileIds, false);
    this.setValidation(form.controls.AfeApprovalTeam, true);
    this.areThereSubtypes = this.helper.matrixService.filteredItems.ProjectSubTypeIds.length >= 1 &&
      this.helper.screenBehaviors.getVisibility(this._elRef, 'ProjectSubTypeIds');

    if (!this.areThereSubtypes) {
      form.patchValue({
        ProjectSubTypeIds: this.helper.screenBehaviors.getDefault(this.selectedProjectType, 'ProjectSubTypeId',
          this.DataSources.ProjectSubTypeIds,
          null, null)
      });
      if (this.subTypeIsRequired === true) {
        this.setValidation(form.controls.ProjectSubTypeIds, true);
      }
      else {
        this.setValidation(form.controls.ProjectSubTypeIds, false);
      }
    }
    else {
      this.setValidation(form.controls.ProjectSubTypeIds, true);
    }
    if (this.selectedProjectType === 1) {
      this.setValidation(form.controls.StartDate, false);
    }
    this.createProjectForm.updateValueAndValidity();
  }

  private resetLookups(applyBlank: boolean = true) {

    //todo: reset all selections
    //other todo:   handle projetTypes, Teams, etc...
    let assetTeamId = this.helper.Selections.assetTeam && this.helper.Selections.assetTeam.AssetTeamId;
    if (applyBlank || (assetTeamId && !this.helper.matrixService.filterValue) ||
      (assetTeamId && assetTeamId !== this.helper.matrixService.filterValue.AssetTeamId)) {
      let pt = this.helper.emptyProjectTemplate(this.helper.Selections.assetTeam && this.helper.Selections.assetTeam.AssetTeamId);
      this.helper.matrixService.filter(pt);
    }
    this.clearSelections();
    this.handleProjectTypeChange(null);
    this.selectSingles();
  }

  private handleExploreDevelopSelection(value: NameValueIntParentInt): void {
    if (value != null && this.selectedExploreDevelopId !== value.value) {
      this.selectedExploreDevelopId = value.value;
      this.helper.Selections.exploreDevelopId = value.value;
      if (value.value) {
        this.setDefaultAfeApprovalProjectTeamList();
      }
    }
  }

  private handleSubTypeSelection(value: NameValueIntParentInt): void {

    if (this.selectedProjectType !== null && value != null && this.helper.Selections.projectSubTypeId !== value.value) {
      this.selectedProjectSubType = value.name;
      this.selectedProjectSubTypeId = value.value;
      this.hideTankBattery = (value.parentValue === ProjectTypeId.PadConstruction && value.value !== PadConstructionProjectSubTypeId.TankBattery);
      this.hidePPID = !this.hideTankBattery;
      this.helper.Selections.projectSubTypeId = value.value;
      if (value.value) {
        this.setDefaultAfeApprovalProjectTeamList();
      }
    }
    if (this.showWellSelectors) {
      let isHorOrVert = value && (value.name === 'Horizontal' || value.name === 'Vertical');
      if (isHorOrVert) {
        this.helper.filterWellProfiles(value.name);
      } else if (this.showWellSelectors) {
        this.helper.filterWellProfiles(null);
      }
      if (this.OtherDataSources.WellProfileIds.length === 1) {
        let newValue = {};
        Object.assign(newValue, this.OtherDataSources.WellProfileIds[0]);
        let control = this.createProjectForm.controls.WellProfileIds;
        if (!control) {
          console.warn('control not found with fieldName: WellProfileIds');
        } else {
          control.patchValue(newValue, { emitEvent: true });
        }
      }
    }
  }

  private handleNonOperatingChange(value) {
    if (value) {
      // Non-Operating = Pioneer Operated is "True".  This means the BusinessAssociate ComboBox is hidden and the form
      // control disabled. "True" The Accounting Tab should be visible.
      this.createProjectForm.controls.BusinessAssociateIds.disable();
      this.isFalseNonOperating = false;

      // Non-Operating = Not Pioneer Operated is "False.  This means the BusinessAssociate ComboBox is displayed and the form
      // control enabled. This is the case for the majority of projects.
    } else {
      this.isFalseNonOperating = true;
      this.createProjectForm.controls.BusinessAssociateIds.enable();
    }
  }

  private clearSelections(emitEvent: boolean = false) {
    let mapOf = this.helper.getAllLists(true);
    this.helper.screenBehaviors.clearSelections(null, this._elRef, mapOf, emitEvent, null, true, true);
  }

  private selectSingles(emitEvent: boolean = false) {
    if (this.selectingSingles) return;
    this.selectingSingles = true;
    try {
      let mapOf = this.helper.getAllLists(true);
      mapOf.delete('TypeCurveAreaIds');
      this.helper.screenBehaviors.selectSingles(this._elRef, mapOf, emitEvent);
    } finally {
      this.selectingSingles = false;
    }
  }

  private filterFunction(value: NameValueInt): void {
    this.helper.matrixService.updateOneItemValueX(this.helper.matrixService.updateFunctions.FunctionId, value && value.value);
    this.selectSingles(true);
    this.refreshDefaultProjectTeamList(value);
  }

  private filterAfeApprovalTeam(value: ProjectApprovalMatrix): void {
    if (value && value !== null) {
      this.selectSingles(true);
    }
  }

  private async filterAssetTeam(assetTeam: AssetTeam): Promise<void> {
    if (!assetTeam) {
      this.helper.Selections.assetTeam = null;
      return;
    }

    if ((this.helper.Selections.assetTeam === assetTeam) ||
      (this.helper.Selections.assetTeam && this.helper.Selections.assetTeam.AssetTeamId === assetTeam.AssetTeamId)) {
      return;
    }

    let pt = this.helper.emptyProjectTemplate(assetTeam.AssetTeamId);
    this.helper.Selections.assetTeam = assetTeam;
    let assetTeamId = assetTeam.AssetTeamId;
    if (
      this.resetMatrix ||
      (assetTeamId && !this.helper.matrixService.filterValue) ||
      (assetTeamId && assetTeamId !== this.helper.matrixService.filterValue.AssetTeamId)) {
      this.resetMatrix = false;
      await this.helper.matrixService.filterAsync(pt);

      this.helper.Selections.assetTeam = assetTeam;
      this.createProjectForm.controls.pioneerAssetTeam.setValue(assetTeam.AssetTeamCode);
      this.helper.filterAssetTeamItems();

      if (!assetTeamId) {
        this.createProjectForm.controls.ProjectTeamIds.disable();
        this.createProjectForm.controls.TypeCurveAreaIds.disable();
      } else {
        this.createProjectForm.controls.TypeCurveAreaIds.enable();
      }

      this.resetLookups(false);
    }
  }

  private filterProjectType(value: NameValueInt): void {
    if (!value) return;
    if (this.helper.matrixService.updateOneItemValueX(this.helper.matrixService.updateFunctions.ProjectTypeId, value && value.value)) {
      this.handleProjectTypeChange(value);
      if (value.value) {
        this.setDefaultAfeApprovalProjectTeamList();
      }
      this.selectSingles(true);
    }
  }

  private filterDivision(value: NameValueInt): void {
    this.helper.matrixService.updateOneItemValueX(this.helper.matrixService.updateFunctions.DivisionId, value && value.value);
    this.selectSingles(true);
  }

  public async handleWellFilter(wellName: string): Promise<any> {
    this.helper.WellFilters.wellName = wellName;

    if (wellName.length > 2) {
      let result = await this.getAssetWells(wellName);
      return result;
    } else {
      return await Promise.resolve([]);
    }
  }

  public async handleDrillGroupFilter(dg: string): Promise<any> {
    if (dg && dg.length > 2) {
      let result = await this.getDrillgroups(dg);
      return result;
    } else {
      return await Promise.resolve([]);
    }
  }

  public async handleTankBatteryAssetsFilter(tba: string): Promise<any> {
    if (tba && tba.length > 2) {
      let result = await this.getTankBatteryAssets(tba);
      return result;
    } else {
      return await Promise.resolve([]);
    }
  }

  public async handlePPIDAssetsFilter(ppid: string): Promise<any> {
    if (ppid && ppid.length > 2) {
      let result = await this.getPPIDAssets(ppid);
      return result;
    } else {
      return await Promise.resolve([]);
    }
  }

  public tbAssetselectionChange(event) {
    if (event && event.TankBatteryDesignId != null) {
      let constructionTypeId = this.TankBatteryDesignToConstructionTypeMapping(event.TankBatteryDesignId)
      this.createProjectForm.controls.ConstructionTypeIds.setValue(this.specialDataSources.ConstructionType.filtered.filter(s => s.value === constructionTypeId)[0]);
    }
    else {
      this.createProjectForm.controls.ConstructionTypeIds.setValue(null);
    }

    this.notifyTBAssetChange.next(event);
  }

  public clearTBAsset(event) {
    if (event) {
      this.createProjectForm.controls.TankBatteryAssetId.setValue(null)
    }
  }

  public onProjectStartEndDateBlur() {
    if (this.showFinishDate) {
      this.createProjectForm.controls.NeedByDate.setValidators([Validators.required, this.endDateAfterStart]);
      this.createProjectForm.controls.NeedByDate.updateValueAndValidity();
    }
  }

  public endDateAfterStart(input: any) {
    if (!input.root || !input.root.controls) {
      return null;
    }
    if (!input.value) {
      return { missingValue: true };
    }
    else {
      if (input.root.controls.StartDate.value > input.value) {
        return { dateMustBeGreaterThanStart: true };
      }
      return null;
    }
  }

  private TankBatteryDesignToConstructionTypeMapping(tankBatteryDesignId) {
    switch (tankBatteryDesignId) {
      case TankBatteryDesign.Large:
        return ConstructionTypes.Large;
      case TankBatteryDesign.Medium:
        return ConstructionTypes.Medium;
      case TankBatteryDesign.Small:
        return ConstructionTypes.Small;
      default:
        return null;
    }
  }
  private resetHelperServiceProjectTypeAndSubtype() {
    this.helper.Selections.projectTypeId = null;
    this.helper.Selections.projectSubTypeId = null
  }

  public updateTBAssets(data) {
    this.selectedAssets = data;
    if (data.length > 0) {
      let divisionId = this.getTankBatteryDivisionId(data);
      if (this.createProjectForm.controls.DivisionIds.value === null)
        this.createProjectForm.controls.DivisionIds.setValue(this.DataSources.DivisionIds.filter(s => s.value === divisionId)[0]);

      let endDate = new Date(Math.min.apply(null, data.map((x) => new Date(x.RigReleaseDate))));
      this.createProjectForm.get('NeedByDate').setValue(moment(endDate).subtract(7, 'day').toDate());
    }
    else {
      this.createProjectForm.get('NeedByDate').setValue(null);
    }
    this.allocatedAssets = data.map((x) => x.AssetId);
    this.createProjectForm.updateValueAndValidity();
  }

  public updatePipelineAssets(data) {
    this.selectedAssets = data;
    if (data.length > 0) {
      if (this.selectedProjectType == ProjectTypeId.Flowline) {
        this.selectedWell = data[0].WellId;
        this.setProjectDefaults();
      }
      else
        this.setProjectEndDateFromAssets();
    }
    else {
      this.createProjectForm.get('NeedByDate').setValue(null);
    }
    this.allocatedAssets = data.map((x) => x.AssetId);
    this.createProjectForm.updateValueAndValidity();
  }

  private setProjectEndDateFromAssets() {
    let endDate = new Date(Math.min.apply(null, this.selectedAssets.map((x) => new Date(x.RigReleaseDate))));
    this.createProjectForm.get('NeedByDate').setValue(moment(endDate).toDate());
  }

  private getTankBatteryDivisionId(well) {
    let jvWell = well.filter(x => x.IsJointVenture)[0];

    if (!jvWell)
      jvWell = well[0];
    let isHoriz = jvWell.WellProfileId === 1;

    if (jvWell.IsJointVenture && jvWell.BasinId === 1)
      return isHoriz ? ProjectDivision.PERMIAN_JV_HORIZONTALS : ProjectDivision.PERMIAN_JV_VERTICALS;
    else if (!jvWell.IsJointVenture && jvWell.BasinId === 1)
      return isHoriz ? ProjectDivision.PERMIAN_NON_JV_HORIZONTALS : ProjectDivision.PERMIAN_NON_JV_VERTICALS;
    else if (jvWell.BasinId === 2)
      return isHoriz ? ProjectDivision.DELAWARE_BASIN_HORIZONTALS : ProjectDivision.DELAWARE_BASIN_VERTICALS;
    else
      return null;
  }

  public handleAssetsWellSelectChange(e: AssetWell): void {
    if (e !== undefined && e !== null) {
      let assetWell: AssetWell = e;
      let smallWell: ISmallWell = {
        WellId: assetWell.WellId,
        Name: assetWell.Name,
        Ssn: assetWell.Ssn,
        AssetId: assetWell.AssetId,
        DrillDesignId: assetWell.DrillDesignId,
        DevelopmentAreaId: assetWell.DevelopmentAreaId,
        TypeCurveAreaId: assetWell.TypeCurveAreaId,
        Asset: {
          AssetName: assetWell.AssetName,
          DevelopmentAreaId: assetWell.DevelopmentAreaId,
          TypeCurveAreaId: assetWell.TypeCurveAreaId
        }
      };
      if (e.WellId !== undefined) {
        if (e.DrillDesignId) {
          if (e.DrillDesignId) {
            this.helper.matrixService.updateOneItemValueX(
              this.helper.matrixService.updateFunctions.DrillDesignId,
              e.DrillDesignId ? e.DrillDesignId : 2
            );
          }
        }
        if (e.AssetId === null || e.AssetId === undefined) {
          this.errorService.error('Asset Id for this well is not available.');
        } else {
          this.helper.WellFilters.assetId = e.AssetId;
        }
        this.helper.WellFilters.well = smallWell;
        this.helper.WellFilters.wellId = assetWell.WellId;
        this.helper.WellFilters.assetId = assetWell.AssetId;
        this.helper.WellFilters.AssetTypeId = assetWell.AssetTypeId;
        this.selectedWell = assetWell.WellId;
        this.setProjectDefaults();
      }
    }
  }

  public projectSubTypeItemDisabled(itemArgs: { dataItem: any, index: number }) {
    return !itemArgs.dataItem.isActive;
  }

  public async AddNewProject(e): Promise<any> {
    this.createProjectFormSubmitted = true;
    if (this.createProjectForm.invalid || (this.isDGRelated && !this.selectedDrillGroup)) {
      return;
    }
    this.status.action = 'Saving Project';
    this.status.running = true;
    let projects: Array<Project>;
    let wait = 1;

    let isPostStimChecked: boolean = this.createProjectForm.controls.CreatePostStimProject.value;
    let isAllWellsChecked: boolean = this.createProjectForm.controls.CreateProjectsForWellsInGroup.value;

    try {
      projects = await this.helper.createNewProjects(isPostStimChecked, isAllWellsChecked, this._ProjectDefaults, this.allocatedAssets, this.selectedDrillGroup, this.additionalDrillgroups)
      this.helper.Selections.projectSubTypeId = null;
      let initialProject = projects.length === 1 ? projects[0] : projects.find(p => p.Name === this.createProjectForm.controls.projectName.value);
      this.callCreateProject(initialProject);
      if (projects.length > 1) {
        projects.forEach(p => {
          this.helper.getProjectView(p.ProjectId)
            .then(pv => {
              this.projectTabService.open(pv, true, false);
            });
        })
      }
    }
    catch (e) {
      this.errorService.error('Error Adding New Project', e);
      this.status.action = 'Error: ' + (e as Error).message;
      wait = 10000;
    }
    finally {
      window.setTimeout(() => {
        //this way the user sees the last message
        this.status.running = false;
      }, wait);
    }
  }

  public handleAfeApprovalTeamChange(approval: ProjectApprovalMatrix): void {
    this.approvalTeamUrl = null;
    if (approval) {
      let projectApproval: ProjectApprovalMatrix = approval;
      this.approvalTeamUrl = `${this.environment.executeUrl}ui/view?AFE_APPROVAL_PATH/${projectApproval.DocumentId}`;
    }
  }
}

export interface IControls {
  projectName: any;
  projectDescription: any;
  Location: any;
  FunctionIds: any;
  AfeApprovalTeam: any;
  pioneerAssetTeam: any;
  ProjectTypeIds: any;
  DivisionIds: any;
  ProjectSubTypeIds: any;
  TypeCurveAreaIds: any;
  ProjectTeamIds: any;
  BusinessAssociateIds: any;
  IsNonOperating: any;
  NeedByDate: any;
  StartDate: any;
  ConstructionSequenceIds: any;
  ConstructionTypeIds: any;
  WellProfileIds: any;
  TargetFormationIds: any;
  WellIds: any;
  DrillGroupIds: any;
  TankBatteryAssetId: any;
  PadAssetId: any;
  CreatePostStimProject: any;
  CreateProjectsForWellsInGroup: any;
  ExploreDevelopIds: any;
  BatteryDesignTypes: any
}

export interface SpecialControls {
  ConstructionType: any;
  ConstructionSequence: any;
}

export function projectNameValidator(nameRe: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const verboten = nameRe.test(control.value);
    return verboten ? { 'invalidName': { value: control.value } } : null;
  };
}

export function projectMinDateValidator(newDate: Date): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const verboten = newDate > control.value;
    // tslint:disable-next-line:indent
    return verboten ? { 'invalidDate': { value: control.value } } : null;
  };
}

export function projectDescriptionLengthValidator(max: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    let encoded = encodeURIComponent(control.value);
    const verboten = encoded && encoded.length > max;
    return verboten ? { 'invalidDescriptionLength': { value: control.value } } : null;
  };
}

export function projectNameInvalidCharValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    let invalid: boolean = false;
    let val = control.value;
    if (val === null || val === undefined) { return null; }
    for (var i = 0; i < val.length; i++) {
      if (val.charCodeAt(i) < 32 || val.charCodeAt(i) > 126) {
        invalid = true;
      }
    }
    return invalid ? { 'invalidCharacters': { value: control.value } } : null;
  };
}